import { useState } from "react";
import { Check, ShoppingCart, X } from "lucide-react";
import { ConditionBadge } from "./ConditionBadge";
import { useCart } from "./CartProvider";
import { formatPrice } from "@/lib/formatPrice";

export interface Product {
  id: number;
  name: string;
  price: number;
  sale_price: number | null;
  condition: number;
  is_new: number;
  image: string;
  size: string | null;
  categoryName: string;
}

const placeholderSvg = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><rect fill="#e8dcc8" width="400" height="400"/><text x="200" y="185" text-anchor="middle" font-family="sans-serif" font-size="48" fill="#1a1a2e">👕</text><text x="200" y="230" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold" fill="#1a1a2e">NEMA SLIKE</text></svg>`)}`;

function handleImgError(e: React.SyntheticEvent<HTMLImageElement>) {
  const el = e.currentTarget;
  el.onerror = null;
  el.src = placeholderSvg;
}

export function ProductCard({ product, index = 0 }: { product: Product; index?: number }) {
  const { items, addItem } = useCart();
  const [dialogOpen, setDialogOpen] = useState(false);
  const inCart = items.some((item) => item.id === product.id);

  function handleAdd() {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      salePrice: product.sale_price,
      image: product.image,
    });
  }

  return (
    <>
      <article className="bg-white rounded-xl overflow-hidden border border-border/60 hover:shadow-lg transition-all duration-300 group">
        {/* Image */}
        <div
          className="relative aspect-3/4 overflow-hidden rounded-t-xl cursor-pointer bg-neutral-100"
          onClick={() => setDialogOpen(true)}
        >
          <img
            src={`/uploads/${product.id}-thumb.webp`}
            alt={product.name}
            loading={index < 8 ? "eager" : "lazy"}
            width={400}
            height={533}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={handleImgError}
          />

          {product.sale_price != null && (
            <div className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
              Akcija
            </div>
          )}

          {!product.is_new && (
            <div className="absolute bottom-2 left-2">
              <ConditionBadge value={product.condition} />
            </div>
          )}
          {product.is_new && (
            <div className="absolute top-2 left-2 bg-foreground text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
              Novo
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">
              {product.categoryName}
            </span>
            {product.size && (
              <>
                <span className="text-[11px] text-muted-foreground">·</span>
                <span className="text-[11px] font-semibold text-foreground/70">{product.size}</span>
              </>
            )}
          </div>
          <h3
            className="text-sm font-semibold text-foreground truncate cursor-pointer hover:text-muted-foreground transition-colors mt-0.5"
            onClick={() => setDialogOpen(true)}
          >
            {product.name}
          </h3>

          <div className="flex items-baseline gap-2 mt-1">
            {product.sale_price != null ? (
              <>
                <span className="text-base font-bold text-foreground">
                  {formatPrice(product.sale_price)} KM
                </span>
                <span className="text-xs text-muted-foreground line-through">
                  {formatPrice(product.price)} KM
                </span>
              </>
            ) : (
              <span className="text-base font-bold text-foreground">
                {formatPrice(product.price)} KM
              </span>
            )}
          </div>

          <button
            onClick={handleAdd}
            disabled={inCart}
            className={`w-full mt-2 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              inCart
                ? "bg-secondary text-muted-foreground cursor-default"
                : "bg-foreground text-white hover:bg-foreground/90"
            }`}
          >
            {inCart ? (
              <>
                <Check className="size-3" />
                U korpi
              </>
            ) : (
              <>
                <ShoppingCart className="size-3" />
                Dodaj
              </>
            )}
          </button>
        </div>
      </article>

      {dialogOpen && (
        <ProductDialog
          product={product}
          inCart={inCart}
          onAdd={handleAdd}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}

function ProductDialog({
  product,
  inCart,
  onAdd,
  onClose,
}: {
  product: Product;
  inCart: boolean;
  onAdd: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        className="relative bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 size-8 rounded-full bg-black/5 hover:bg-black/10 transition-colors flex items-center justify-center cursor-pointer"
        >
          <X className="size-4" />
        </button>

        {/* Image */}
        <div className="aspect-3/4 overflow-hidden bg-neutral-100 rounded-t-2xl">
          <img
            src={`/uploads/${product.id}-display.webp`}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={handleImgError}
          />
        </div>

        {/* Details */}
        <div className="p-5 space-y-4">
          <span className="inline-block text-[11px] text-muted-foreground uppercase tracking-wider font-medium bg-secondary px-2.5 py-1 rounded-full">
            {product.categoryName}
          </span>

          <h2 className="text-xl font-semibold text-foreground leading-snug">
            {product.name}
          </h2>

          {/* Price */}
          <div className="flex items-baseline gap-3">
            {product.sale_price != null ? (
              <>
                <span className="text-2xl font-bold text-foreground">
                  {formatPrice(product.sale_price)} KM
                </span>
                <span className="text-sm text-muted-foreground line-through">
                  {formatPrice(product.price)} KM
                </span>
                <span className="text-xs font-medium text-red-500">
                  Uštedi {formatPrice(product.price - product.sale_price)} KM
                </span>
              </>
            ) : (
              <span className="text-2xl font-bold text-foreground">
                {formatPrice(product.price)} KM
              </span>
            )}
          </div>

          {!product.is_new && (
            <p className="text-xs text-red-500 font-medium">
              Zamjene i povrati nisu mogući
            </p>
          )}

          {(product.size || !product.is_new) && (
            <div className="flex items-center gap-4">
              {product.size && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Veličina:</span>
                  <span className="text-sm font-semibold">{product.size}</span>
                </div>
              )}
              {!product.is_new && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Stanje oštećenja:</span>
                  <ConditionBadge value={product.condition} />
                </div>
              )}
            </div>
          )}

          {/* Divider + info */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>Plaćanje pouzećem</span>
              <span>·</span>
              <span>Jedinstveni artikal</span>
            </div>
          </div>

          {/* Add to cart */}
          <button
            onClick={onAdd}
            disabled={inCart}
            className={`w-full rounded-xl py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              inCart
                ? "bg-secondary text-muted-foreground cursor-default"
                : "bg-foreground text-white hover:bg-foreground/90"
            }`}
          >
            {inCart ? (
              <>
                <Check className="size-4" />
                Već je u korpi
              </>
            ) : (
              <>
                <ShoppingCart className="size-4" />
                Dodaj u korpu
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
